import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X, Target, CheckCircle, Tag, Info } from 'lucide-react';
import api from '../utils/api';


const ClientScopesSection = ({ clientId }) => {
  const [scopes, setScopes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('select'); // 'select', 'custom', 'edit'
  const [editingScope, setEditingScope] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', template_id: null });
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [scopesResponse, templatesResponse] = await Promise.all([
        api.get(`/clients/${clientId}/scopes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get(`/settings/scope-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setScopes(scopesResponse.data.scopes || []);
      setTemplates(templatesResponse.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching scopes:', error);
      setMessage({ type: 'error', text: 'Failed to load scopes' });
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      fetchData();
    }
  }, [clientId, fetchData]);

  const handleAddFromTemplate = async (template) => {
    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        name: template.name,
        description: template.description || '',
        template_id: template.id,
        is_custom: false
      };

      await api.post(`/clients/${clientId}/scopes`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ type: 'success', text: 'Scope added successfully' });
      fetchData();
      setShowModal(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error adding scope:', error);
      setMessage({ type: 'error', text: 'Failed to add scope' });
    }
  };

  const handleCustomSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      
      if (editingScope) {
        // Update existing scope
        const payload = {
          name: formData.name,
          description: formData.description || ''
        };
        await api.put(`/clients/${clientId}/scopes/${editingScope.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Scope updated successfully' });
      } else {
        // Create custom scope
        const payload = {
          name: formData.name,
          description: formData.description || '',
          is_custom: true
        };
        await api.post(`/clients/${clientId}/scopes`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: 'Custom scope created successfully' });
      }

      fetchData();
      resetModal();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving custom scope:', error);
      setMessage({ type: 'error', text: 'Failed to save scope' });
    }
  };

  const handleDelete = async (scopeId) => {
    if (!window.confirm('Are you sure you want to remove this scope from the client?')) return;

    try {
      const token = localStorage.getItem('access_token');
      await api.delete(`/clients/${clientId}/scopes/${scopeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Scope removed successfully' });
      fetchData();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error deleting scope:', error);
      setMessage({ type: 'error', text: 'Failed to remove scope' });
    }
  };

  const handleEdit = (scope) => {
    setEditingScope(scope);
    setFormData({ 
      name: scope.name, 
      description: scope.description, 
      template_id: scope.template_id
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const resetModal = () => {
    setFormData({ name: '', description: '', template_id: null });
    setEditingScope(null);
    setModalMode('select');
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Project Scopes</h3>
          <p className="text-sm text-gray-600 mt-1">
            Define the scope of work contracted for this client.
          </p>
        </div>
        <button
          onClick={() => { setModalMode('select'); setShowModal(true); }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Scope
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>Workflow Integration:</strong> Scopes inherit request type mappings from their templates (configured in Company Settings → Scope Templates). 
          Workflows can check if portal submissions are within this client&apos;s contracted scope of work.
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`rounded-lg p-3 ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {/* Scopes List */}
      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
        {scopes.length === 0 ? (
          <div className="text-center py-8">
            <Target className="mx-auto h-10 w-10 text-gray-400" />
            <h4 className="mt-2 text-sm font-medium text-gray-900">No scopes defined</h4>
            <p className="mt-1 text-sm text-gray-500">Add scopes to define the work covered for this client.</p>
          </div>
        ) : (
          scopes.map((scope) => (
            <div key={scope.id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <Target className="h-5 w-5 text-purple-600 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h4 className="text-base font-medium text-gray-900">{scope.name}</h4>
                        {scope.template_name && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            From: {scope.template_name}
                          </span>
                        )}
                        {scope.is_custom && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            Custom
                          </span>
                        )}
                      </div>
                      {scope.description && (
                        <p className="mt-1 text-sm text-gray-600">{scope.description}</p>
                      )}
                      
                      {/* Show inherited request type mappings from template (read-only) */}
                      {scope.request_type_mappings && scope.request_type_mappings.length > 0 && (
                        <div className="mt-2">
                          <span className="text-xs text-gray-500 mr-2">Covers:</span>
                          <div className="inline-flex flex-wrap gap-1">
                            {scope.request_type_mappings.map((type, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <Tag className="w-3 h-3 mr-1" />
                                {type}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  {scope.is_custom && (
                    <button
                      onClick={() => handleEdit(scope)}
                      className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(scope.id)}
                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {modalMode === 'edit' ? 'Edit Scope' : modalMode === 'custom' ? 'Create Custom Scope' : 'Add Scope'}
                </h3>
                <button onClick={resetModal} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {modalMode === 'select' && (
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Select from Templates</h4>
                    <p className="text-xs text-gray-500 mb-3">
                      Templates include pre-configured request type mappings for workflow automation.
                    </p>
                    {templates.length === 0 ? (
                      <p className="text-sm text-gray-500">No templates available. Create templates in Settings first.</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {templates.map((template) => {
                          const isAdded = scopes.some(s => s.template_id === template.id);
                          return (
                            <div
                              key={template.id}
                              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                isAdded
                                  ? 'border-green-300 bg-green-50'
                                  : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                              }`}
                              onClick={() => !isAdded && handleAddFromTemplate(template)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <h5 className="text-sm font-medium text-gray-900">{template.name}</h5>
                                    {isAdded && <CheckCircle className="w-4 h-4 text-green-600" />}
                                  </div>
                                  {template.description && (
                                    <p className="text-xs text-gray-600 mt-1">{template.description}</p>
                                  )}
                                  {/* Show template's request type mappings */}
                                  {template.request_type_mappings && template.request_type_mappings.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                      {template.request_type_mappings.map((type, idx) => (
                                        <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                                          {type}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="pt-4 border-t">
                    <button
                      onClick={() => setModalMode('custom')}
                      className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:border-purple-400 hover:text-purple-600 transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Custom Scope
                    </button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Custom scopes don&apos;t inherit template mappings. Configure their request type mappings in Company Settings → Scope Templates.
                    </p>
                  </div>
                </div>
              )}

              {(modalMode === 'custom' || modalMode === 'edit') && (
                <form onSubmit={handleCustomSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Scope Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Custom Integration Work"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe what this scope includes..."
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={resetModal}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {modalMode === 'edit' ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientScopesSection;
